// File: frontend/src/pages/parent/ChildFees.jsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/axios';
import FeeReceipt from '../fees/FeeReceipt';

const ChildFees = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  /**
   * Fetch child fee collection history
   */
  const fetchFeeHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/v1/parent/fees');
      if (response.data?.success) {
        setData(response.data.data);
      } else {
        setError(response.data?.message || 'Failed to load fee history.');
      }
    } catch (err) {
      console.error('Error fetching child fee history:', err);
      setError(err.response?.data?.message || err.message || 'Error loading fee history.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeeHistory();
  }, [fetchFeeHistory]);

  const handlePrintReceipt = (item) => {
    const formattedReceipt = {
      receipt_no: item.receipt_no || `RCP-${item.id}`,
      payment_date: item.created_at || item.date || new Date().toISOString(),
      payment_method: item.payment_mode || 'Cash',
      student_name: item.student_name || data?.child?.student_name || 'Student',
      student_code: item.student_code || data?.child?.student_id || `STD-${item.student_id || 101}`,
      father_name: item.father_name || data?.child?.father_name || '',
      class_id: item.class_name || data?.child?.class_name || '10-A',
      total_amount: item.total_amount || parseFloat(item.paid_amount || 0) + parseFloat(item.balance || 0),
      discount_amount: item.discount_amount || 0,
      fine_amount: item.fine_amount || 0,
      paid_amount: item.paid_amount || 0,
      balance_amount: item.balance || 0,
      remarks: item.remarks || 'Tuition fee payment'
    };
    setSelectedReceipt(formattedReceipt);
  };

  const summary = data?.summary || { total_paid: 0, total_pending: 0, fee_status: 'Clear' };
  const history = data?.history || [];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/parent/dashboard" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Child Fee Collections & Payments</h1>
              <p className="text-xs text-slate-500">Monthly tuition fees & official receipts</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        
        {/* Fee Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">Total Fee Paid</span>
            <h3 className="text-3xl font-extrabold text-emerald-600 font-mono">
              ${summary.total_paid.toFixed(2)}
            </h3>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">Pending Balance</span>
            <h3 className="text-3xl font-extrabold text-red-600 font-mono">
              ${summary.total_pending.toFixed(2)}
            </h3>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
            <span className="text-xs font-semibold text-slate-500 uppercase block mb-1">Fee Account Status</span>
            <h3 className={`text-2xl font-extrabold capitalize ${
              summary.fee_status === 'Clear' ? 'text-emerald-600' : 'text-red-600'
            }`}>
              {summary.fee_status}
            </h3>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-800 rounded-xl text-sm flex items-center justify-between shadow-sm">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-bold text-red-600">✕</button>
          </div>
        )}

        {/* Fee History Table Card */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-bold text-slate-900 text-base">Tuition Fee Ledger History</h3>
            <span className="text-xs text-slate-500">Records: {history.length}</span>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-400 font-medium">Loading fee collection ledger...</div>
          ) : history.length === 0 ? (
            <div className="p-12 text-center text-slate-400 font-medium">
              No fee payment records found for your child.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs font-sans">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 uppercase font-semibold">
                    <th className="py-3.5 px-6">Receipt No</th>
                    <th className="py-3.5 px-4">Period</th>
                    <th className="py-3.5 px-4 text-right">Total Amount</th>
                    <th className="py-3.5 px-4 text-right">Paid Amount</th>
                    <th className="py-3.5 px-4 text-right">Balance</th>
                    <th className="py-3.5 px-4 text-center">Status</th>
                    <th className="py-3.5 px-6 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700 font-medium">
                  {history.map((fc) => (
                    <tr key={fc.id} className="hover:bg-slate-50/80 transition">
                      <td className="py-4 px-6 font-mono font-bold text-primary">
                        {fc.receipt_no || `RCP-${fc.id}`}
                      </td>
                      <td className="py-4 px-4 font-bold text-slate-900">
                        Month {fc.month}, {fc.year}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-slate-800">
                        ${parseFloat(fc.total_amount || (parseFloat(fc.paid_amount) + parseFloat(fc.balance))).toFixed(2)}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-emerald-600 font-bold">
                        ${parseFloat(fc.paid_amount).toFixed(2)}
                      </td>
                      <td className="py-4 px-4 text-right font-mono text-red-600 font-bold">
                        ${parseFloat(fc.balance).toFixed(2)}
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold capitalize ${
                          fc.status === 'paid' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                          fc.status === 'partial' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                          'bg-red-100 text-red-800 border border-red-300'
                        }`}>
                          {fc.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => handlePrintReceipt(fc)}
                          className="bg-primary hover:bg-blue-600 text-white font-semibold px-3 py-1.5 rounded-lg transition text-xs flex items-center gap-1.5 justify-end ml-auto shadow-sm"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                          </svg>
                          <span>Print Receipt</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </main>

      {/* Official Printable Receipt Modal */}
      {selectedReceipt && (
        <FeeReceipt
          receiptData={selectedReceipt}
          onClose={() => setSelectedReceipt(null)}
        />
      )}
    </div>
  );
};

export default ChildFees;
